import {
  CompiereDataGridFilterModel,
  CompiereDataGridFilterType,
  CompiereDataGridRequestJSON,
  CompiereDataGridSortModelType,
  CompiereDataGridType
} from '@compiere-ws/models/compiere-data-json';
import { ColumnFilterAutocomplete, OperatorFilterAutocomplete } from '@iupics-components/models/autocomplete-interfaces';
import { filterOperators, OperatorFilterType } from '@iupics-components/models/universal-filter';
import { AppConfig } from '@iupics-config/app.config';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';
import * as moment from 'moment';

export class UniversalFilterUtils {
  private static config: AppConfig;
  private static connectorService: SecurityManagerService;
  private static translator: TranslateService;

  public static setConfig(config: AppConfig) {
    this.config = config;
  }

  public static setConnectorService(connectorService: SecurityManagerService) {
    this.connectorService = connectorService;
  }

  public static setTranslateService(translator: TranslateService) {
    this.translator = translator;
  }

  /*
   * *****************************************************************
   * *****************************************************************
   * *****************************************************************
   * **************************** CHIPS ******************************
   * *****************************************************************
   * *****************************************************************
   * *****************************************************************
   */
  public static dataToDisplayToChips(dataToDisplay: DataToDisplay): FilterChip[] {
    const dataToTransform = this.cleanDataToDisplay(cloneDeep(dataToDisplay));
    // @custo_code
    let dateFormatIso_code = this.connectorService.getIupicsDefaultLanguage().iso_code;//Redmine 127611
    if(dateFormatIso_code=='en_US'){
      moment.locale('fr_FR');
    }else{
      moment.locale(dateFormatIso_code);
    }  //End Redmine 127611
    const filterChips: FilterChip[] = [];
    dataToTransform.filters.forEach((f, index) => {
      let filterStr: string;
      let operatorStr: string;
      if (f.operator.operator) {
        if (f.operator.operator.isRange) {
          if (f.filter && f.filterTo) {
            filterStr =
              (f.operator.operator.filterType === CompiereDataGridFilterType.DATE
                ? moment(f.filter).format('YYYY-MM-DDTHH:mm:ss.SSS').substring(0, 10)
                : f.filter) +
              (this.translator ? this.translator.instant('universalFilter.and') : ' and ') +
              (f.operator.operator.filterType === CompiereDataGridFilterType.DATE
                ? moment(f.filterTo).format('YYYY-MM-DDTHH:mm:ss.SSS').substring(0, 10)
                : f.filterTo);
            operatorStr = f.operator.displayValue;
          } else if (f.filter && !f.filterTo) {
            filterStr =
              f.operator.operator.filterType === CompiereDataGridFilterType.DATE
                ? moment(f.filter).format('YYYY-MM-DDTHH:mm:ss.SSS').substring(0, 10)
                : f.filter;
            operatorStr = filterOperators.find(
              (op) => op.type === OperatorFilterType.BIGGER_EQUALS && op.filterType === f.column.columnInfo.filterType
            ).label;
          } else if (!f.filter && f.filterTo) {
            filterStr =
              f.operator.operator.filterType === CompiereDataGridFilterType.DATE
                ? moment(f.filterTo).format('YYYY-MM-DDTHH:mm:ss.SSS').substring(0, 10)
                : f.filterTo;
            operatorStr = filterOperators.find(
              (op) => op.type === OperatorFilterType.LESS_EQUALS && op.filterType === f.column.columnInfo.filterType
            ).label;
          }
        } else if (f.filter !== null && f.filter !== undefined) {
          if (Array.isArray(f.filter)) {
            filterStr = f.filter
              .map((item) => (item.displayValue !== undefined ? item.displayValue : item))
              .join(this.translator ? this.translator.instant('universalFilter.or') : ' or ');
            operatorStr = f.operator.displayValue;
          } else {
            filterStr =
              f.operator.operator.filterType === CompiereDataGridFilterType.DATE
                ? moment(f.filter).format('YYYY-MM-DDTHH:mm:ss.SSS').substring(0, 10)
                : f.filter.displayValue
                ? f.filter.displayValue
                : f.filter;
            operatorStr = f.operator.displayValue;
          }
        }
        if (filterStr !== null && filterStr !== undefined && operatorStr !== null && operatorStr !== undefined) {
          filterChips.push({
            icon: 'icon-ico-filter',
            displayValue: `${f.column.displayValue} ${operatorStr} '${filterStr}'`,
            type: 'filters',
            index: index,
            columnName: f.column.id
          });
        }
      }
    });
    dataToTransform.groups.forEach((group, index) => {
      if (group && group.displayValue) {
        filterChips.push({
          icon: 'icon-ico-group-check',
          displayValue: group.displayValue,
          type: 'groups',
          index: index
        });
      }
    });
    dataToTransform.sortings.forEach((sorting, index) => {
      if (sorting && sorting.column && sorting.column.displayValue) {
        filterChips.push({
          icon:
            sorting.sortingType === CompiereDataGridSortModelType.ASC
              ? 'icon-ico-tri-az'
              : sorting.sortingType === CompiereDataGridSortModelType.DESC
              ? 'icon-ico-tri-za'
              : 'icon-ico-tri',
          displayValue: sorting.column.displayValue,
          type: 'sortings',
          index: index
        });
      }
    });
    return filterChips;
  }

  /*
   * *****************************************************************
   * *****************************************************************
   * *****************************************************************
   * ********************** DATA TO DISPLAY **************************
   * *****************************************************************
   * *****************************************************************
   * *****************************************************************
   */

  public static compiereDataGridToDataToDisplay(
    columnFilters: { items: ColumnFilterAutocomplete[] },
    operatorFilters: { items: OperatorFilterAutocomplete[] },
    filters: CompiereDataGridRequestJSON[]
  ): DataToDisplay[] {
    const dataToDisplay: DataToDisplay[] = [];
    filters.forEach((f) => {
      const data: DataToDisplay = {
        favorite: f.label,
        // groups: f.rowGroupCols.map(groupCol =>
        //   columnFilters.items.find(columnFilter => parseInt(groupCol.id, 10) === columnFilter.id)
        // ),
        groups: f.rowGroupCols.map((groupCol) => columnFilters.items.find((columnFilter) => groupCol.id === columnFilter.id)),
        sortings: f.sortModel.map((sortModel) => ({
          column: columnFilters.items.find(
            (columnName) => sortModel.colId === columnName.columnInfo.fieldEntity.field.ColumnName
          ),
          sortingType: sortModel.sort
        })),
        filters: []
      };

      data.filters = this.filterModelToFilterToDisplay(columnFilters, operatorFilters, f);
      dataToDisplay.push(data);
    });
    return dataToDisplay;
  }

  public static filterModelToFilterToDisplay(
    columnFilters: { items: ColumnFilterAutocomplete[] },
    operatorFilters: { items: OperatorFilterAutocomplete[] },
    f: CompiereDataGridRequestJSON
  ): FilterToDisplay[] {
    const filterToDisplays: FilterToDisplay[] = [];
    if (f.filterModel) {
      Object.keys(f.filterModel).forEach((columnKey) => {
        const base: FilterToDisplay = {
          column: columnFilters.items.find(
            (columnFilter) =>
              columnFilter.columnInfo.fieldEntity.field.ColumnName === columnKey ||
              columnFilter.columnInfo.fieldEntity.field.ColumnSQL === columnKey
          ),
          filter: undefined,
          operator: undefined
        };
        const filterModel = f.filterModel[columnKey];
        if (
          filterModel.operators.length > 0 &&
          filterModel.values.length > 0 &&
          filterModel.operators.length === filterModel.values.length
        ) {
          for (let i = 0; i < filterModel.operators.length; i++) {
            const operator: OperatorFilterType = filterModel.operators[i];
            const filterToDisplay: FilterToDisplay = Object.assign({}, base);
            filterToDisplay.operator = operatorFilters.items.find(
              (operatorFilter) =>
                operatorFilter.operator.type === operator &&
                operatorFilter.operator.filterType === f.filterModel[columnKey].filterType
            );
            let values = filterModel.values[i];
            if (
              (filterModel.filterType === CompiereDataGridFilterType.NUMBER ||
                filterModel.filterType === CompiereDataGridFilterType.SET) &&
              !Array.isArray(values) &&
              (operator === OperatorFilterType.EQUALS || operator === OperatorFilterType.NOT_EQUALS)
            ) {
              values = [values + ''];
            }
            filterToDisplay.filter = values;
            if (operator === 'between') {
              filterToDisplay.filterTo = filterModel.values[++i];
            }
            filterToDisplays.push(filterToDisplay);
          }
        }
      });
    }
    return filterToDisplays;
  }
  public static sortModelToFilterToDisplay(
    columnFilters: { items: ColumnFilterAutocomplete[] },
    s: CompiereDataGridRequestJSON
  ): SorterToDisplay[] {
    const sorterToDisplays: SorterToDisplay[] = [];
    if (s.sortModel) {
      s.sortModel.forEach((sortModelTmp) => {
        const base: SorterToDisplay = {
          column: columnFilters.items.find(
            (columnFilter) => columnFilter.columnInfo.fieldEntity.field.ColumnName === sortModelTmp.colId
          ),
          sortingType: undefined
        };
        const sortToDisplay: SorterToDisplay = Object.assign({}, base);
        sortToDisplay.sortingType = sortModelTmp.sort;
        sorterToDisplays.push(sortToDisplay);
      });
    }
    return sorterToDisplays;
  }

  public static groupModelToFilterToDisplay(
    columnFilters: { items: ColumnFilterAutocomplete[] },
    s: CompiereDataGridRequestJSON
  ): ColumnFilterAutocomplete[] {
    const groupToDisplays: ColumnFilterAutocomplete[] = [];
    if (s.rowGroupCols) {
      s.rowGroupCols.forEach((groupColTmp) => {
        const base = columnFilters.items.find((columnFilter) => columnFilter.id === groupColTmp.id);
        const groupToDisplay: ColumnFilterAutocomplete = Object.assign({}, base);
        groupToDisplays.push(groupToDisplay);
      });
    }
    return groupToDisplays;
  }

  /*
   * *****************************************************************
   * *****************************************************************
   * *****************************************************************
   * ********************* COMPIERE DATA GRID ************************
   * *****************************************************************
   * *****************************************************************
   * *****************************************************************
   */
  public static dataToDisplayToCompiereDataGrid(dataToDisplay: DataToDisplay, tabID: number): CompiereDataGridRequestJSON {
    const dataToTransform: DataToDisplay = this.transformDisplayOperators(cloneDeep(dataToDisplay));
    dataToTransform.filters = dataToTransform.filters.filter(
      (f) =>
        (f.filter !== undefined && f.filter !== null && f.filter !== '') ||
        (f.filterTo !== undefined && f.filterTo !== null && f.filterTo !== '')
    );
    return {
      label: dataToTransform.favorite,
      filterModel: this.filterToDisplayToFilterModel(dataToTransform),
      startRow: 0,
      endRow: this.config.getConstant('GridTabInfinityScrollUiComponent#cacheBlockSize'),
      entityId: tabID,
      windowType: CompiereDataGridType.WINDOW,
      rowGroupCols: dataToTransform.groups
        .filter((group) => group && group.id !== -1)
        .map((group) => ({
          id: group.id,
          displayName: group.displayValue,
          field: group.columnInfo.fieldEntity.field.ColumnName,
          aggFunc: ''
        })),
      sortModel: dataToTransform.sortings
        .filter((sorting) => sorting.column && sorting.column.id !== -1)
        .map((sorting) => ({
          colId: sorting.column.columnInfo.fieldEntity.field.ColumnName,
          sort: sorting.sortingType
        }))
    };
  }

  public static filterToDisplayToFilterModel(
    dataToTransform: DataToDisplay
  ): {
    [columnName: string]: CompiereDataGridFilterModel;
  } {
    const filterModel: { [columnName: string]: CompiereDataGridFilterModel } = {};
    dataToTransform.filters
      .filter((f) => f.column.id !== -1 && f.column.displayValue)
      .forEach((f) => {
        const columnName = f.column.columnInfo.fieldEntity.ColumnName
          ? f.column.columnInfo.fieldEntity.ColumnName
          : f.column.columnInfo.fieldEntity.field.ColumnName;
        const values =
          f.operator.operator.filterType === CompiereDataGridFilterType.DATE
            ? moment(f.filter).format('YYYY-MM-DDTHH:mm:ss.SSS').substring(0, 26) +
              '' +
              moment(f.filter)
                .format('YYYY-MM-DDTHH:mm:ss.SSS')
                .substring(27, moment(f.filter).format('YYYY-MM-DDTHH:mm:ss.SSS').length)
            : Array.isArray(f.filter)
            ? f.filter.map((item) => (item instanceof Object ? item.id : item))
            : f.filter instanceof Object
            ? f.filter.id
            : f.operator.operator.type === 'contains'
            ? f.filter.trim()
            : f.filter;
        if (filterModel.hasOwnProperty(columnName)) {
          filterModel[columnName].values.push(values);
          filterModel[columnName].operators.push(f.operator.operator.type);
        } else {
          filterModel[columnName] = {
            filterType: f.operator.operator.filterType,
            values: [values],
            operators: [f.operator.operator.type]
          };
        }
        if (f.operator.operator.filterType === CompiereDataGridFilterType.NUMBER) {
          for (let i = 0; i < filterModel[columnName].values.length; i++) {
            if (Array.isArray(filterModel[columnName].values[i])) {
              filterModel[columnName].values[i] = filterModel[columnName].values[i].map((value) => {
                if (typeof value === 'string') {
                  value = value.replace(',', '.');
                }
                if (parseFloat(value) !== NaN) {
                  value = parseFloat(value);
                } else {
                  value = 0;
                }
                return value;
              });
            } else {
              if (filterModel[columnName].values[i] instanceof String) {
                filterModel[columnName].values[i] = filterModel[columnName].values[i].replace(',', '.');
              }
              if (parseFloat(filterModel[columnName].values[i]) !== NaN) {
                filterModel[columnName].values[i] = parseFloat(filterModel[columnName].values[i]);
              } else {
                filterModel[columnName].values[i] = 0;
              }
            }
          }
        }
      });
    return filterModel;
  }

  private static transformDisplayOperators(dataToDisplay: DataToDisplay): DataToDisplay {
    if (dataToDisplay.filters.filter((f) => f.column.id === -1 || f.operator.id === -1).length === 0) {
      const betweens = dataToDisplay.filters
        .map((f) => (f.operator.operator.type === 'between' ? f : undefined))
        .filter((f) => f !== undefined);

      betweens.forEach((f) => {
        const index = dataToDisplay.filters.findIndex((fil) => fil === f);
        if (f.filter !== undefined && f.filterTo !== undefined && f.filter !== null && f.filterTo !== null) {
          const borneInf: FilterToDisplay = {
            column: f.column,
            filter: f.filter,
            operator: f.operator
          };
          const borneSup: FilterToDisplay = {
            column: f.column,
            filter: f.filterTo,
            operator: f.operator
          };
          dataToDisplay.filters.splice(index, 1, borneInf, borneSup);
        } else if (f.filter !== undefined && f.filter !== null && (f.filterTo === undefined || f.filterTo === null)) {
          const operator = filterOperators.find(
            (op) => op.type === OperatorFilterType.BIGGER_EQUALS && op.filterType === f.column.columnInfo.filterType
          );
          const borneInf: FilterToDisplay = {
            column: f.column,
            filter: f.filter,
            operator: {
              id: operator.id,
              displayValue: operator.label,
              operator: operator
            }
          };
          dataToDisplay.filters.splice(index, 1, borneInf);
        } else if ((f.filter === undefined || f.filter === null) && f.filterTo !== undefined && f.filterTo !== null) {
          const operator = filterOperators.find(
            (op) => op.type === OperatorFilterType.LESS_EQUALS && op.filterType === f.column.columnInfo.filterType
          );
          const borneSup: FilterToDisplay = {
            column: f.column,
            filter: f.filterTo,
            operator: {
              id: operator.id,
              displayValue: operator.label,
              operator: operator
            }
          };
          dataToDisplay.filters.splice(index, 1, borneSup);
        }
      });
    }
    return cloneDeep(dataToDisplay);
  }

  /*
   * ************************************************************
   * ************************************************************
   * ************************************************************
   * ******************** FILTER UTILS **************************
   * ************************************************************
   * ************************************************************
   * ************************************************************
   */

  public static cleanDataToDisplay(dataToDisplay: DataToDisplay) {
    dataToDisplay.filters = dataToDisplay.filters.filter(
      (filter) =>
        filter.column &&
        filter.operator &&
        (filter.column.id !== -1 ||
          filter.operator.id !== -1 ||
          filter.filter !== '' ||
          (filter.operator.id !== -1 && filter.operator.operator.isRange && filter.filterTo !== ''))
    );
    dataToDisplay.groups = dataToDisplay.groups.filter((group) => group.id !== -1);
    dataToDisplay.sortings = dataToDisplay.sortings.filter((sorting) => sorting.column && sorting.column.id !== -1);
    return dataToDisplay;
  }
}

export interface DataToDisplay {
  favorite: string;
  groups: ColumnFilterAutocomplete[];
  filters: FilterToDisplay[];
  sortings: SorterToDisplay[];
}

export interface SorterToDisplay {
  column: ColumnFilterAutocomplete;
  sortingType: CompiereDataGridSortModelType;
}

export interface FilterToDisplay {
  column: ColumnFilterAutocomplete;
  operator: OperatorFilterAutocomplete;
  filter: any;
  filterTo?: any;
}

export interface FilterChip {
  displayValue: string;
  icon: string;
  type: string;
  index: number;
  columnName?: string;
}
